import React, { useState, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const TableOfContents = ({ contentData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);

  // Fonction pour extraire les titres H2 du contenu HTML
  const extractH2Titles = (htmlContent) => {
    if (
      !htmlContent ||
      typeof document === "undefined" ||
      typeof htmlContent !== "string"
    )
      return [];

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const h2Elements = tempDiv.querySelectorAll("h2");

    return Array.from(h2Elements).map((h2) => h2.textContent.trim());
  };

  // Créer un tableau avec les éléments qui ont des titres et leurs index originaux
  const itemsWithTitles = [];
  if (contentData) {
    contentData.forEach((item, originalIndex) => {
      let hasTitle = false;
      switch (item.fieldGroupName) {
        case "post_Acfpageinterne_Contenudynamique_Texte":
        case "page_Acfpageinterne_Contenudynamique_Texte":
          hasTitle =
            item.texte?.titre ||
            extractH2Titles(typeof item.texte === "string" ? item.texte : "")
              .length > 0;
          break;
        case "post_Acfpageinterne_Contenudynamique_Texteimage":
        case "page_Acfpageinterne_Contenudynamique_Texteimage":
          hasTitle =
            item.texteimage?.titre ||
            extractH2Titles(typeof item.texte === "string" ? item.texte : "")
              .length > 0;
          break;
        case "post_Acfpageinterne_Contenudynamique_Conseil":
        case "page_Acfpageinterne_Contenudynamique_Conseil":
          hasTitle = item.conseil?.titre;
          break;
        case "post_Acfpageinterne_Contenudynamique_Lien":
        case "page_Acfpageinterne_Contenudynamique_Lien":
          hasTitle = item.lien?.titre;
          break;
        default:
          hasTitle = false;
      }

      if (hasTitle) {
        itemsWithTitles.push({ item, originalIndex });
      }
    });
  }

  // Effet pour détecter la section actuelle lors du scroll
  useEffect(() => {
    if (itemsWithTitles.length === 0 || typeof window === "undefined") return;

    const handleScroll = () => {
      const headerHeight = 140;
      const scrollPosition = window.scrollY + headerHeight + 100; // +100 pour plus de marge

      let currentSectionFound = null;

      // Créer un tableau des positions des sections avec titres
      const sectionsWithTitles = itemsWithTitles
        .map(({ originalIndex }) => {
          const element = document.querySelector(
            `[data-content-index="${originalIndex}"]`
          );
          return {
            index: originalIndex,
            top: element ? element.offsetTop : 0,
          };
        })
        .sort((a, b) => a.top - b.top);

      // Trouver la section actuelle
      for (let i = sectionsWithTitles.length - 1; i >= 0; i--) {
        if (scrollPosition >= sectionsWithTitles[i].top) {
          currentSectionFound = sectionsWithTitles[i].index;
          break;
        }
      }

      setCurrentSection(currentSectionFound);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Appeler une fois au montage pour définir l'état initial

    return () => window.removeEventListener("scroll", handleScroll);
  }, [contentData, itemsWithTitles.length]);

  if (itemsWithTitles.length === 0) {
    return null;
  }

  const scrollToElement = (index, type, title) => {
    if (typeof document === "undefined" || typeof window === "undefined")
      return;
    const element = document.querySelector(`[data-content-index="${index}"]`);
    if (element) {
      let targetElement = element;

      // Si c'est un titre H2, chercher le h2 spécifique dans l'élément
      if (type === "h2") {
        const h2Elements = element.querySelectorAll("h2");
        for (let h2 of h2Elements) {
          if (h2.textContent.trim() === title) {
            targetElement = h2;
            break;
          }
        }
      }

      const headerHeight = 140; // Hauteur du header fixe
      const elementPosition = targetElement.offsetTop - headerHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  const getItemTitle = (item) => {
    switch (item.fieldGroupName) {
      case "post_Acfpageinterne_Contenudynamique_Texte":
      case "page_Acfpageinterne_Contenudynamique_Texte":
        return (
          item.texte?.titre ||
          extractH2Titles(
            typeof item.texte === "string" ? item.texte : ""
          )[0] ||
          null
        );
      case "post_Acfpageinterne_Contenudynamique_Texteimage":
      case "page_Acfpageinterne_Contenudynamique_Texteimage":
        return (
          item.texteimage?.titre ||
          extractH2Titles(
            typeof item.texte === "string" ? item.texte : ""
          )[0] ||
          null
        );
      case "post_Acfpageinterne_Contenudynamique_Conseil":
      case "page_Acfpageinterne_Contenudynamique_Conseil":
        return item.conseil?.titre;
      case "post_Acfpageinterne_Contenudynamique_Lien":
      case "page_Acfpageinterne_Contenudynamique_Lien":
        return item.lien?.titre;
      default:
        return null;
    }
  };

  return (
    <div className="table-of-contents-inner">
      <button className="toc-toggle" onClick={() => setIsExpanded(!isExpanded)}>
        <span>Sommaire</span>
        <ChevronDownIcon
          style={{
            width: "16px",
            height: "16px",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        />
      </button>

      <nav
        className="toc-nav"
        style={{ display: isExpanded ? "block" : "none" }}
      >
        <ul className="toc-list">
          {itemsWithTitles.map(({ item, originalIndex }, index) => {
            const titles = [];

            // Ajouter le titre principal s'il existe
            const mainTitle = getItemTitle(item);
            if (mainTitle) {
              titles.push({ title: mainTitle, type: "main" });
            }

            // Ajouter les titres H2 s'ils existent
            let h2Titles = [];
            switch (item.fieldGroupName) {
              case "post_Acfpageinterne_Contenudynamique_Texte":
              case "page_Acfpageinterne_Contenudynamique_Texte":
                h2Titles = extractH2Titles(
                  typeof item.texte === "string" ? item.texte : ""
                );
                break;
              case "post_Acfpageinterne_Contenudynamique_Texteimage":
              case "page_Acfpageinterne_Contenudynamique_Texteimage":
                h2Titles = extractH2Titles(
                  typeof item.texte === "string" ? item.texte : ""
                );
                break;
              default:
                h2Titles = [];
            }

            h2Titles.forEach((h2Title, h2Index) => {
              if (h2Title !== mainTitle) {
                titles.push({ title: h2Title, type: "h2" });
              }
            });

            return titles.map((titleObj, titleIndex) => {
              const isCurrent = currentSection === originalIndex;
              return (
                <li key={`${originalIndex}-${titleIndex}`} className="toc-item">
                  <button
                    className={`toc-link ${isCurrent ? "current" : ""}`}
                    onClick={() =>
                      scrollToElement(
                        originalIndex,
                        titleObj.type,
                        titleObj.title
                      )
                    }
                  >
                    <span className="toc-title">{titleObj.title}</span>
                  </button>
                </li>
              );
            });
          })}
        </ul>
      </nav>
    </div>
  );
};

export default TableOfContents;
